"use client"

import { Button } from "../../../components/ui/button"
import { Printer } from "lucide-react"
import { usePDF } from "react-to-pdf"
import { useEffect, useState } from "react"
import { useRemoteClientGet } from "@/configs/http-client"

interface TrainingSheetButtonProps {
  idTreino: number
  idUnidade: number,
  nameStudant?: string
}

export function TrainingSheetButton({ idTreino, idUnidade, nameStudant }: TrainingSheetButtonProps) {
  const { remoteClientGet } = useRemoteClientGet()
  const { toPDF, targetRef } = usePDF({
    filename: `${idTreino}-ficha-treino.pdf`,
    page: {
      margin: 20,
      format: "a4",
      orientation: "portrait",
    },
    canvas: {
      mimeType: "image/png",
      qualityRatio: 1,
    },
    overrides: {
      // Ignore images that cause issues
      canvas: {
        ignoreElements: (element) => {
          return element.tagName === "IMG" && (element as HTMLImageElement).src.includes("placeholder.svg")
        },
      },
    },
  })
  const [isPrinting, setIsPrinting] = useState(false)
  const [trainingData, setTrainingData] = useState<any>(null)
  const [isLoading, setIsLoading] = useState(false)

  useEffect(() => {
    const generatePDF = async () => {
      if (isPrinting && trainingData) {
        try {
          await toPDF()
        } catch (error) {
          console.error("Error generating PDF:", error)
          alert("Erro ao gerar PDF. Verifique se não há imagens problemáticas.")
        } finally {
          setIsPrinting(false)
        }
      }
    }

    generatePDF()
  }, [isPrinting, trainingData, toPDF])

  const handlePrint = async () => {
    if (idTreino && idUnidade) {
      setIsLoading(true)

      try {
        const response = await remoteClientGet(`fichas-treino/${idTreino}?idUnidade=${idUnidade}`)

        if (response.status < 200 || response.status >= 300) {
          throw new Error(`Failed to fetch training data: ${response.status}`)
        }

        setTrainingData(response.data.data)

        // Add a small delay to ensure DOM is updated
        setTimeout(() => {
          setIsPrinting(true)
        }, 100)
      } catch (error) {
        console.error("Error fetching training data:", error)
        alert("Erro ao gerar PDF. Tente novamente.")
      } finally {
        setIsLoading(false)
      }
    }
  }

  const formatDate = (dateString: string) => {
    if (!dateString) return ""
    const date = new Date(dateString)
    return new Intl.DateTimeFormat("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    }).format(date)
  }

  const today = new Intl.DateTimeFormat("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  }).format(new Date())

  return (
    <div>
      {/* Hidden printable area */}
      <div
        ref={targetRef}
       className={`
    w-[794px] h-[1123px] p-10 bg-white
    shadow-lg rounded-none m-0
    fixed left-1/2 top-[200%] transform -translate-x-1/2
    ${isPrinting ? "block" : "hidden"}
  `}
      >
        {trainingData && (
          <div className="space-y-6">
            {/* Header */}
            <div className="flex justify-between items-center border-b pb-4">
              <div>
                <h1 className="text-2xl font-bold">{trainingData.unidade?.nomeFantasia || "Nome da Unidade"}</h1>
                <p className="text-sm text-gray-500">
                  {trainingData.unidade?.endereco?.cidade || "Cidade"} -{" "}
                  {trainingData.unidade?.endereco?.estado || "Estado"}
                </p>
                <p className="text-sm text-gray-500">Tel: {trainingData.unidade?.contato?.telefone || "Telefone"}</p>
              </div>
              <div className="text-right">
                <h2 className="text-xl font-semibold">Ficha de Treino</h2>
                <p className="text-sm text-gray-500">Data: {today}</p>
                <p className="text-sm text-gray-500">Ficha #{trainingData?.idFicha || "--"}</p>
              </div>
            </div>

            {/* Training Info */}
            <div className="flex flex-col items-start border-b pb-4 ">
              <h3 className="text-lg font-semibold mb-2">Informações do Aluno</h3>
              <p>
                <span className="font-medium">Nome do Aluno:</span> {nameStudant || "--"}
              </p>

            </div>
            <div className="border-b pb-4">

              <h3 className="text-lg font-semibold mb-2">Informações do Treino</h3>
              <p>
                <span className="font-medium">Nome do Treino:</span> {trainingData.nome || "--"}
              </p>
              <p>
                <span className="font-medium">ID do Treino:</span> {trainingData.id || "--"}
              </p>
              <p>
                <span className="font-medium">Data de Criação:</span>{" "}
                {trainingData.inclusao ? formatDate(trainingData.inclusao) : "--"}
              </p>
            </div>

            {/* Exercises */}
            <div>
              <h3 className="text-center font-semibold mb-4">Exercícios</h3>
              <table className="w-full border-collapse">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-2">Exercício</th>
                    <th className="text-center py-2">Execução</th>
                    <th className="text-center py-2">Séries</th>
                    <th className="text-center py-2">Repetições</th>
                    <th className="text-center py-2">Carga ({trainingData.exercicios?.[0]?.tipo || "KG"})</th>
                    <th className="text-center py-2">Descanso</th>
                    <th className="text-right py-2">Observações</th>
                  </tr>
                </thead>
                <tbody>
                  {trainingData.exercicios?.length > 0 ? (
                    trainingData.exercicios.map((exercise: any) => (
                      <tr key={exercise.id || Math.random()} className="border-b">
                        <td className="py-3 font-medium">{exercise.exercicio?.nome || "Sem nome"}</td>
                        <td className="py-3 text-center">{exercise.velocidadeExecucao || "-"}</td>
                        <td className="py-3 text-center">{exercise.serie ?? "-"}</td>
                        <td className="py-3 text-center">{exercise.repeticoes ?? "-"}</td>
                        <td className="py-3 text-center">{exercise.carga ?? "-"}</td>
                        <td className="py-3 text-center">
                          {exercise.tempoPausaSeries ? `${exercise.tempoPausaSeries}s` : "60s"}
                        </td>
                        <td className="py-3 text-right">
                          {exercise.observacao || exercise.exercicio?.descricao || "-"}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={6} className="py-3 text-center text-gray-400">
                        Nenhum exercício encontrado.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Footer */}
            <div className="mt-8 pt-4 border-t text-center text-sm text-gray-500">
              <p>Este treino foi gerado pelo sistema AceleraFit</p>
              <p>{trainingData.unidade?.contato?.email || "Email não disponível"}</p>
            </div>
          </div>
        )}
      </div>

      {/* Button */}
      <Button
        variant="ghost"
        size="icon"
        className="h-8 mr-2 flex gap-2 w-full"
        onClick={handlePrint}
        disabled={isLoading}
      >
        <Printer className="h-4 w-4 text-gray-400" />
      </Button>
    </div>
  )
}
